import type { MenuItemType } from "core/entities";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useLocale } from "core/hooks/useLocale";
import { useMemo } from "react";

// APB doesn't allow for undefined url parameter, so we're treating "#" as undefined
export const URL_UNDEFINED_CHARACTER = "#";

export function useMenuItems(menuItemType: MenuItemType = "Header") {
  const locale = useLocale();
  const { data, isLoading, isError, mutate } = useCentrixApi({
    path: "/api/app/menu-item/v2",
    parameters: {
      query: {
        locale,
      },
    },
    swrOptions: {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  });

  const menuItems = useMemo(() => {
    const items = data
      ?.filter((item) => item.menuItemType === menuItemType)
      .map(({ docRootNodes, url, submenus: categories, ...rest }) => {
        const processedLibraries = docRootNodes || [];
        const sectionURL = url === URL_UNDEFINED_CHARACTER ? undefined : url;
        const processedCategories =
          categories?.map((category) => {
            const categoryURL =
              category.url === URL_UNDEFINED_CHARACTER
                ? undefined
                : category.url;
            return {
              ...category,
              url: categoryURL,
              submenus: category.submenus,
            };
          }) ?? [];
        return {
          libraries: processedLibraries,
          url: sectionURL,
          submenus: processedCategories,
          ...rest,
        };
      });
    return items ?? [];
  }, [data, menuItemType]);
  return { data, isLoading, isError, mutate, menuItems };
}
